import { useCallback, useEffect, useState } from 'react';
import type { AssetView } from '@mantelpiece/shared';
import { api, type StoredSession } from '../lib/api';

interface Props {
  readonly session: StoredSession;
  /** Assets already in the album, so they are shown as taken rather than offered. */
  readonly taken: readonly string[];
  readonly onAdd: (assetIds: readonly string[]) => Promise<void>;
}

/**
 * Everything this account has ever uploaded, to any frame, in one grid.
 *
 * This is the view the asset/placement split bought: before it there was no
 * "your photos", only "photos on that frame", and adding a picture to a second
 * place meant finding the original file again.
 */
export const LibraryPicker = ({ session, taken, onAdd }: Props) => {
  const [assets, setAssets] = useState<readonly AssetView[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [chosen, setChosen] = useState<readonly string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (before?: number) => {
      setLoading(true);
      try {
        const result = await api.listLibrary(session.token, before);
        setAssets((current) => (before ? [...current, ...result.assets] : result.assets));
        setCursor(result.nextCursor);
      } finally {
        setLoading(false);
      }
    },
    [session.token],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = (id: string) =>
    setChosen((current) =>
      current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
    );

  const add = async () => {
    setBusy(true);
    try {
      await onAdd(chosen);
      setChosen([]);
    } finally {
      setBusy(false);
    }
  };

  const offered = assets.filter((asset) => !taken.includes(asset.id));

  if (loading && assets.length === 0) return <p className="empty">Loading your photos</p>;
  if (offered.length === 0) {
    return (
      <p className="empty">
        Everything you have uploaded is already in this album. Add more from the picker above.
      </p>
    );
  }

  return (
    <div className="stack">
      <div className="section-head">
        <p className="eyebrow">
          Your photos · {chosen.length > 0 ? `${chosen.length} chosen` : 'tap to choose'}
        </p>
        <button
          type="button"
          className="button button-small"
          disabled={chosen.length === 0 || busy}
          onClick={() => void add()}
        >
          {busy ? 'Adding' : `Add ${chosen.length || ''}`.trim()}
        </button>
      </div>

      <ul className="sheet">
        {offered.map((asset) => {
          const on = chosen.includes(asset.id);
          return (
            <li key={asset.id} className="print" data-state="static" data-picked={on}>
              <button
                type="button"
                className="print-pick"
                aria-pressed={on}
                onClick={() => toggle(asset.id)}
              >
                <span className="print-image">
                  <img src={asset.thumbUrl} alt="" loading="lazy" decoding="async" />
                </span>
                <span className="visually-hidden">
                  {on ? 'Chosen' : 'Choose this photo'}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {cursor !== null && (
        <p>
          <button
            type="button"
            className="button button-quiet button-small"
            disabled={loading}
            onClick={() => void load(cursor)}
          >
            {loading ? 'Loading' : 'Load older'}
          </button>
        </p>
      )}
    </div>
  );
};
